import { paths } from '@/schemas/microcredit';
import axios from '@/shared/axios';
import {
  NeedCloseAccountRegister,
  CloseAccountStatus,
  ResourceQestionTypes,
  FaqTypes,
  IssueOrderStatus,
  IssueOrderSourceType,
} from '@/shared/constants';
import { FundProviders } from '@/shared/funds';

export interface QueryAreaList {
  Result: {
    provinceList: Record<string, string>;
    cityList: Record<string, string>;
    countyList: Record<string, string>;
  };
}

/**
 * 获取省市列表
 */
export function queryAreaList(): Promise<QueryAreaList['Result']> {
  return axios.get('/bank/province/city/list');
}

/**
 * 获取APP信息
 */
export interface QueryAppReleaseInfo {
  Params: {
    platform: number;
  };
  Result: {
    version: string;
    onlineTime: string;
    appSize: number;
  };
}

export function queryAppReleaseInfo(
  params: QueryAppReleaseInfo['Params'],
): Promise<QueryAppReleaseInfo['Result']> {
  return axios.get('/app/version/toH5', { params });
}

export interface QueryRedirectAfterAuthorize {
  Params: {
    identity?: string;
    channel?: string;
    orderId?: string;
    resource?: string;
    routingSource?: string;
    [key: string]: any;
  };
}

/**
 * 查询授权后重定向地址
 */
export function queryRedirectAfterAuthorize(
  appName: string,
  params?: QueryRedirectAfterAuthorize['Params'],
): Promise<string> {
  return axios.post(
    '/redirect/url',
    {
      routingSource: appName,
      ...(params ?? {}),
    },
    {
      __interceptorOptions__: {
        security: true,
      },
    },
  );
}

export interface SaveClueInfo {
  Params: {
    captchCode: string;
    carNo: string;
    city: string;
    customerName: string;
    loanAmount: number;
    phone: string;
    platform: string;
    province: string;
    purpose: string;
    randText: string;
    sourceCode: string;
    type: string;
  };
}

/**
 * 保存导流搜索页信息
 */
export function saveClueInfo(params: SaveClueInfo['Params']): Promise<void> {
  return axios.post('/yrd/clue/save', params);
}

/**
 * 天下信用个人数据报告保存
 */
export function saveTxxyCreditReport(params: Record<string, any>) {
  return axios.post('/txxy/dataReport', params);
}

export type Switches = Record<string, 'on' | 'off' | number | boolean> & {
  cashierPaymentCouponTypeSet?: string[];
} & { customerPageConfigSet?: string[] };

/**
 * 获取开关设置
 */
export function querySwitches(
  name?: string | string[],
  options?: { timeout?: number },
): Promise<Switches> {
  return axios.post(
    '/common/switch/setting',
    // 接口暂时不支持通过实验变量获取其结果，只会统一返回所有实验变量
    // { name },
    null,
    {
      timeout: options?.timeout ?? 3000,
      __interceptorOptions__: {
        security: true,
      },
    },
  );
}

export interface QuestionItem {
  answer: string;
  question: string;
}

export interface QueryResourceQuestions {
  Result: {
    id: number;
    imgUrl: string;
    name: string;
    slogan: string;
    type: ResourceQestionTypes;
    questionVOList: QuestionItem[];
  };
}

/**
 * 获取联系客服中问题资源位
 */
export function queryResourceQuestions(
  types?: ResourceQestionTypes | ResourceQestionTypes[],
): Promise<QueryResourceQuestions['Result']> {
  return axios.post('/customer/service/resource/questions', {
    types: Array.isArray(types) ? types.join(',') : types,
  });
}

export interface QueryQuestionList {
  Params: {
    id: number;
  };
}
/**
 * 获取问题列表
 */
export function queryQuestionList(params: QueryQuestionList['Params']): Promise<QuestionItem[]> {
  return axios.post('/customer/service/question/query', params);
}

export interface QueryCanDelete {
  Result: {
    canDelete: boolean;
    reason: string;
    customerLevel: string;
  };
}

/**
 * 获取用户是否可注销的状态
 */
export function queryCanDelete(): Promise<QueryCanDelete['Result']> {
  return axios.get('/user/cancel/status');
}

/**
 * 获取用户当前授信状态
 */
export function queryCreditStatus(): Promise<CloseAccountStatus> {
  return axios.post('/user/cancel/audit/status');
}

export interface QuestionnaireItem {
  answerContent: string;
  answerTitle: string;
  auditStatus: CloseAccountStatus;
}

export interface SaveQuestionnaire {
  Params: {
    userSurveyList: QuestionnaireItem[];
  };
}

/**
 * 提交注销问卷
 */

export type SaveQuestionnaireType = DefineService<paths, '/user/cancel/survey/submit'>;
export function saveQuestionnaire(
  params: SaveQuestionnaireType['Params'],
): Promise<SaveQuestionnaireType['Result']> {
  return axios.post('/user/cancel/survey/submit', params);
}

export interface CloseAccount {
  Params: {
    phone: string | number;
    verifyCode: string | number;
  };
  Result: {
    canDelete: boolean;
    reason: string;
  };
}

/**
 * 注销
 */
export function closeAccount(params: CloseAccount['Params']): Promise<CloseAccount['Result']> {
  return axios.post('/user/cancel', params);
}

/**
 * 判断是否要登记
 */
export function isCloseAccountRegister(): Promise<NeedCloseAccountRegister> {
  return axios.post('/user/cancel/registe/status');
}

export interface CloseAccountRegister {
  Params: {
    phone: string | number;
    verifyCode: string | number;
  };
}

/**
 * 注销登记
 */
export function closeAccountRegister(params: CloseAccountRegister['Params']): Promise<void> {
  return axios.post('/user/cancel/save/registe', params);
}

export interface RetainPackItem {
  type: 1 | 2 | 3; //1-生活类权益包，2-履约宝，3-虚拟提额
  slogan: string;
  desc: string;
  amount: number;
  endTime: string;
}
export interface CloseAccountDetainmentInfo {
  Result: {
    choice: Record<string, any>;
    callLog: boolean;
    bossOrderLog: boolean;
    retainPackList: RetainPackItem[];
    retainPack: RetainPackItem;
  };
}
/**
 * 注销挽留信息
 */
export function closeAccountDetainmentInfo(): Promise<CloseAccountDetainmentInfo['Result']> {
  return axios.post('/user/cancel/choice');
}

export interface DrawDetainmentGift {
  Params: {
    choice: string;
    callLog: boolean;
    bossOrderLog: boolean;
  };
}
/**
 * 领取注销礼包
 */
export function drawDetainmentGift(params: DrawDetainmentGift['Params']) {
  return axios.post('/user/cancel/second/receive/retain/pack', params);
}

export interface StockSendSmsCode {
  Params: {
    phone: string;
  };
}

/**
 * 存量用户发手机验证码
 */

export function stockSendSmsCode(params: StockSendSmsCode['Params']): Promise<void> {
  return axios.post('/user/agreement/stock/phone/auth3/smsCode/request', params);
}

export interface StockAuth {
  Params: {
    userId: number;
    mobile: string;
    userName: string;
    identityId: string;
    verifyCode: string;
  };
}

/**
 * 存量用户三要素鉴权
 */

export function stockAuth(params: StockAuth['Params']): Promise<void> {
  return axios.post('/user/agreement/stock/phone/auth3', params);
}

export interface StockSupplement {
  Params: {
    userId: string;
    updateTimeStamp: string;
  };
}

export interface AgreementItem {
  contractHtml: string;
  contractName: string;
  fileType: number;
  force: boolean;
  textType: 'HTML' | 'URL' | 'PDF_URL';
}

export interface PreviewAgreement {
  Params: {
    updateTimeStamp: string;
    userId: string;
  };
  Result: {
    pdfUrl: string;
    htmlData: AgreementItem[];
    supplementAgreementStatus: number;
  };
}
/**
 * 存量用户协议预览
 */
export function stockSupplementPreview(
  params: PreviewAgreement['Params'],
): Promise<PreviewAgreement['Result']> {
  return axios.post('/user/agreement/stock/supplement/preview', params);
}

/**
 * 存量用户协议签署
 */
export function stockSupplementSign(params: StockSupplement['Params']): Promise<void> {
  return axios.post('/user/agreement/stock/supplement/sign', params);
}

export interface LifePackListItem {
  icon: string;
  type: number;
  title: string;
  titleStyle: string;
  carmi: string;
}
/**
 *注销挽留券包落地页-获取用户当前生活类卡券
 */
export function queryUserLifePackList(): Promise<LifePackListItem[]> {
  return axios.get('/user/message/life/pack/list');
}
export interface QueryLetterUserInfo {
  name: string;
  contractIds: string;
  comName: string;
  lentDate: string;
  debtTransferDate: string;
  rongdanName: string;
}
/**
 * 获取站内信用户名&合同号
 */
export function queryLetterUserInfoTozrxt(): Promise<QueryLetterUserInfo> {
  return axios.post('/user/message/getContractInfo/zrxt');
}
/**
 * 获取站内信用户名&合同号
 */
export function queryLetterUserInfo(params: {
  fundProvided?: FundProviders;
  userMessageId?: string;
}): Promise<QueryLetterUserInfo> {
  return axios.get('/user/message/getContractInfo', {
    params,
  });
}

/**
 * 获取站内信V2.0
 */
export type QueryLetterMessageType = DefineService<paths, '/user/message/getInSiteMsgInfo', 'get'>;
export function queryLetterMessage(
  params: QueryLetterMessageType['Params'],
): Promise<QueryLetterMessageType['Result']> {
  return axios.get('/user/message/getInSiteMsgInfo', {
    params,
  });
}

export interface SubmitIssueFeedback {
  Params: {
    orderId: number;
    verifyCode: string;
    contactPhone: string;
    description: string;
    imageUrls: string[];
  };
}
/**
 * 提交用户工单问题反馈
 */
export function submitIssueFeedback(params: SubmitIssueFeedback['Params']): Promise<void> {
  return axios.post('/boss/order/user/message', params, {
    __interceptorOptions__: {
      security: true,
    },
  });
}

export interface UploadIssueFeedbackFiles {
  Params: {
    file: string;
    orderId: number;
    verifyCode: string;
  };
  Result: {
    fileType: string;
    fileUrl: string;
    showUrl: string;
    fileName: string;
  };
}

/**
 * 上传工单问题反馈图片
 */
export function uploadIssueFeedbackFiles(
  params: UploadIssueFeedbackFiles['Params'],
): Promise<UploadIssueFeedbackFiles['Result']> {
  return axios.post('/boss/order/user/message/upload', params);
}

export interface TicketListItem {
  couponId: number;
  type: number;
  couponValue: number;
  typeDescription: string;
  effectiveTimeEnd: string;
  overAmount: string;
  description: string;
  couponValueReLoan: string;
}
export interface AgainLoanTicket {
  Params: {
    couponPackId: string;
  };
  Result: {
    couponDetailBOList: TicketListItem[];
    status: number;
  };
}
/**
 * 查询复贷用户优惠券
 */
export function queryAgainLoanTicket(
  params: AgainLoanTicket['Params'],
): Promise<AgainLoanTicket['Result']> {
  return axios.post('/coupon/reLoan/activity/list', params);
}

export interface QueryAgainLoanTicket {
  Params: {
    couponPackId: string;
  };
}

/**
 * 查询复贷用户优惠券
 */
export function receiveAgainLoanTicket(params: QueryAgainLoanTicket['Params']): Promise<void> {
  return axios.post('/coupon/reLoan/activity/receive', params);
}

export interface FaqItem {
  id: number;
  type: number;
  name: string;
  slogan: string;
  imgUrl: string;
  questionVOList: [
    {
      id: number;
      answer: string;
      jumpLink?: string | undefined;
      jumpType?: number | undefined;
      question: string;
      questionButton?: string | undefined;
    },
  ];
}

export type FaqMap = Record<FaqTypes, FaqItem[]>;
export type QueryFaqType = DefineService<paths, '/customer/service/resource/questionList'>;
/**
 * 获取联系客服中问题资源位
 * @param {String} types 问题类型
 */
export function queryResourceFaq(types: string): Promise<FaqMap> {
  return axios.post('/customer/service/resource/questionList', {
    types,
  });
}

/**
 * 查询联系客服的配置
 */
export function queryCustomerServiceConfig() {
  return axios.post('/customer/service/contact/entry/config');
}

export interface QueryGrandInfo {
  Params: {
    resourceId: number;
  };
  Result: {
    htmlInfo: string;
  };
}

/**
 * 获取品牌详情
 */
export function queryBrandInfo(
  params: QueryGrandInfo['Params'],
): Promise<QueryGrandInfo['Result']> {
  return axios.get('/boss/activity/brand/info', { params });
}

/**
 * 强核身前检查状态
 */
export type CheckPreEnforceVerifyType = DefineService<paths, '/boss/willFace/preCheck'>;
export function checkPreEnforceVerify(
  params: CheckPreEnforceVerifyType['Params'],
): Promise<CheckPreEnforceVerifyType['Result']> {
  return axios.post('/boss/willFace/preCheck', params);
}

/**
 * 查询强核身链接
 */
export type QueryEnforceVerifyUrlType = DefineService<paths, '/boss/willFace/startUrl'>;
export function queryEnforceVerifyUrl(
  params: QueryEnforceVerifyUrlType['Params'],
): Promise<QueryEnforceVerifyUrlType['Result']> {
  return axios.post('/boss/willFace/startUrl', params);
}

/**
 * 发送强核身验证码
 */
export type SendEnforceVerifyCaptchaType = DefineService<paths, '/boss/willFace/smsCode/request'>;
export function sendEnforceVerifyCaptcha(
  params: SendEnforceVerifyCaptchaType['Params'],
): Promise<SendEnforceVerifyCaptchaType['Result']> {
  return axios.post('/boss/willFace/smsCode/request', params);
}

/**
 * 提交强核身验证码
 */
export type SubmitEnforceVerifyCaptchaType = DefineService<paths, '/boss/willFace/smsCode/verify'>;
export function submitEnforceVerifyCaptcha(
  params: SubmitEnforceVerifyCaptchaType['Params'],
): Promise<SubmitEnforceVerifyCaptchaType['Result']> {
  return axios.post('/boss/willFace/smsCode/verify', params);
}

/**
 * 提交强核身受托人身份信息
 */
export type SubmitEnforceVerifyForAssigneeType = DefineService<
  paths,
  '/boss/willFace/submitEntrustInfo'
>;
export function submitEnforceVerifyForAssignee(
  params: SubmitEnforceVerifyForAssigneeType['Params'],
): Promise<SubmitEnforceVerifyForAssigneeType['Result']> {
  return axios.post('/boss/willFace/submitEntrustInfo', params);
}

/**
 * 查询通用枚举列表项
 */
export type QueryEnumsType = DefineService<paths, '/common/enum/list'>;
export function queryEnums(): Promise<QueryEnumsType['Result']> {
  return axios.post('/common/enum/list');
}

/**
 * 客户征信异议机构名称枚举
 */
export interface QueryAppealCompany {
  Result: {
    appealCompany: Record<string, string>[];
  };
}
export function queryAppealCompany(): Promise<QueryAppealCompany['Result']> {
  return axios.post('/boss/getTypeMapping');
}

/**
 * 客户征信异议申诉保存
 */
export interface SubmitDisputeInfo {
  Params: {
    sendParams: FormData;
    headersInfo?: Record<string, any>;
  };
  Result: {
    id: string;
    message?: string;
  };
}
export function submitDisputeInfo(
  params: SubmitDisputeInfo['Params'],
): Promise<SubmitDisputeInfo['Result']> {
  const headersInfo = params.headersInfo ? params.headersInfo : {};
  return axios.post('/boss/submitInfo', params.sendParams, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...headersInfo,
    },
  });
}

/**
 * 客户征信异议-检查是否存在正在审核中的申诉（登录情况下）
 */

export function hasAudit(params: any): Promise<{ id: string }> {
  return axios.post('/boss/hasAudit?appealCompany=' + params, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

/**
 * 客户征信异议申诉-补充材料
 */

export function supplyMaterial(params: {
  sendParams: FormData;
  headersInfo?: Record<string, any>;
}): Promise<{ message?: string }> {
  const headersInfo = params.headersInfo ? params.headersInfo : {};
  return axios.post('/boss/supplyMaterial', params.sendParams, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...headersInfo,
    },
  });
}

/**
 * 查询最近10条举报信息
 */
export type QueryFraudHistoryType = DefineService<paths, '/boss/complainReport/history'>;
export function queryFraudHistory(): Promise<QueryFraudHistoryType['Result']> {
  return axios.post('/boss/complainReport/history');
}

/**
 * 查询通用枚举列表项
 */
export type QueryFraudType = DefineService<paths, '/boss/complainReport/getTypeMapping'>;
export function queryFraudType(): Promise<QueryFraudType['Result']> {
  return axios.post('/boss/complainReport/getTypeMapping');
}

/**
 * 提交诈骗举报
 */
export type SubmitFraudType = DefineService<paths, '/boss/complainReport/submitInfo'>;
export function submitFraud(params: FormData): Promise<SubmitFraudType['Result']> {
  return axios.post('/boss/complainReport/submitInfo', params, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

/**
 * 消保获取客服服务进度列表 TODO:
 * @returns
 */
// export type CustomerServiceRecords = DefineService<paths, '/boss/order/server_progress/list'>;
export type CustomerServiceRecordsItem = {
  orderId: number;
  createAt: string;
  orderSourceType: IssueOrderSourceType;
  orderType: IssueOrderStatus;
};
export type CustomerServiceRecords = {
  Params: {
    page: number;
    pageSize: number;
  };
  Result: {
    records: CustomerServiceRecordsItem[];
    total: number;
    size: number;
    totalPage: number;
    pages: number;
    current: number;
    extra: Record<string, any>;
  };
};
export function queryCustomerServiceRecords(
  params: CustomerServiceRecords['Params'],
): Promise<CustomerServiceRecords['Result']> {
  return axios.post('/boss/order/server_progress/list', params);
}
export type CustomerServiceRecordDetailRecordVos = {
  talkDetail: string; // 回复信息
  createDate: string; // 记录创建时间
  optType: number; // 操作类型，可以为 null
  extra: string | null; // 附加信息，可以为 null
  replyRole: number; // 回复角色
  proveList: string[];
  supplementContent: string;
};
export type CustomerServiceRecordDetail = {
  Result: {
    orderId: number; // 订单 ID
    orderType: number; // 订单类型
    createAt: string; // 创建时间
    recordVos: CustomerServiceRecordDetailRecordVos[]; // 回复记录数组
    evalLevel: number; // 评价等级，可以为 null
    remainingSupplementCount: number; // 剩余补充次数，可以为 null
    remainingProveAddCount: number; // 剩余证明次数
  };
};
export function queryCustomerServiceRecordDetail(
  orderId: number,
): Promise<CustomerServiceRecordDetail['Result']> {
  return axios.post(`/boss/order/server_progress/detail/${orderId}`);
}

export type IssueProofImageUpload = {
  Params: {
    file: string;
    orderId: string;
  };
  Result: {
    url: string;
    name: string;
    id: number;
  };
};
/**
 * 消保-服务进度-文件上传
 * @param params
 * @returns
 */
export function issueProofImageUpload(params: any): Promise<IssueProofImageUpload['Result']> {
  return axios.post('/boss/order/server_progress/prove_upload_file', params, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

export type AddIssueProof = DefineService<paths, '/boss/order/server_progress/prove_add'>;
/**
 * 消保-服务进度-证明上传
 * @param params
 * @returns
 */
export function addIssueProof(params: AddIssueProof['Params']): Promise<void> {
  return axios.post('/boss/order/server_progress/prove_add', params);
}

/**
 * 消保-服务进度-补充反馈
 * @param params
 * @returns
 */
export type AddIssueSupplement = DefineService<paths, '/boss/order/server_progress/supplement'>;
export function addIssueSupplement(params: AddIssueSupplement['Params']): Promise<void> {
  return axios.post('/boss/order/server_progress/supplement', params);
}

/**
 * 消保-服务进度-服务评价
 * @param params
 * @returns
 */
export type AddIssueEvalLevel = DefineService<paths, '/boss/order/server_progress/eval'>;
export function addIssueEvalLevel(params: AddIssueEvalLevel['Params']): Promise<void> {
  return axios.post('/boss/order/server_progress/eval', params);
}

/**
 * 客服中心
 */
export type QueryCustomerServiceCenterType = DefineService<paths, '/customer/service/center'>;
export type VideoGuideType = QueryCustomerServiceCenterType['Result']['videoItems'];
export function queryCustomerServiceCenter(): Promise<QueryCustomerServiceCenterType['Result']> {
  return axios.post('/customer/service/center');
}

/**
 * 获取配置中心
 */
export type QueryConfigCenterType = DefineService<paths, '/common/config/center'>;
export function queryConfigCenter(
  params: QueryConfigCenterType['Params'],
): Promise<QueryConfigCenterType['Result']> {
  return axios.post('/common/config/center', params);
}

/**
 * 获取注销问卷
 */
export type QueryQuestionnaireType = DefineService<paths, '/user/cancel/survey/query'>;
export function queryQuestionnaire(
  params: QueryQuestionnaireType['Params'],
): Promise<QueryQuestionnaireType['Result']> {
  return axios.post('/user/cancel/survey/query', params);
}

/**
 * 轮询提交注销问卷后的挽留策略
 */
export type QueryDetainmentStrategyType = DefineService<paths, '/user/cancel/survey/queryResult'>;
export function queryDetainmentStrategy(options?: {
  loading?: boolean;
}): Promise<QueryDetainmentStrategyType['Result']> {
  return axios.post('/user/cancel/survey/queryResult', null, {
    __interceptorOptions__: {
      loading: options?.loading ?? true,
    },
  });
}

/**
 * 获取客服热线配置
 */
export type QueryCustomerHotlineConfigType = DefineService<paths, '/customer/service/hotline'>;
export function queryCustomerHotlineConfig(): Promise<QueryCustomerHotlineConfigType['Result']> {
  return axios.post('/customer/service/hotline');
}

/**
 * 注销关闭账号  通知接口加入营销黑名单
 */
export type AddMarketingBlacklistType = DefineService<paths, '/user/cancel/addBL'>;
export function addMarketingBlacklist(): Promise<void> {
  return axios.post('/user/cancel/addBL');
}
/**
 * 查询黑产枚举列表项
 */
export type QueryBlackIndustryType = DefineService<
  paths,
  '/boss/blackIndustry/report/getTypeMapping'
>;
export function queryBlackIndustryType(): Promise<QueryBlackIndustryType['Result']> {
  return axios.post('/boss/blackIndustry/report/getTypeMapping');
}
/**
 * 消保-黑产举报-文件上传
 * @param params
 * @returns
 */
export type BlackIndustryImageUpload = DefineService<
  paths,
  '/boss/blackIndustry/report/upload/file'
>;
export function blackIndustryImageUpload(params: any): Promise<BlackIndustryImageUpload['Result']> {
  return axios.post('/boss/blackIndustry/report/upload/file', params, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
}

/**
 * 提交黑产举报表
 */
export type SubmitBlackIndustryReportType = DefineService<
  paths,
  '/boss/blackIndustry/report/submitInfo'
>;
export function submitBlackIndustryRepor(
  params: SubmitBlackIndustryReportType['Params'],
): Promise<SubmitBlackIndustryReportType['Result']> {
  return axios.post('/boss/blackIndustry/report/submitInfo', params);
}

/**
 * 信用报告引导页
 */
export type QueryCreditReportGuideType = DefineService<paths, '/redirect/report'>;
export function queryCreditReportGuide(): Promise<QueryCreditReportGuideType['Result']> {
  return axios.post('/redirect/report');
}

export interface QueryUseRightsPageUrl {
  Params: {
    couponPackType: number;
  };
}

/**
 * 获取行权页地址
 */
export function queryUseRightsPageUrl(params: QueryUseRightsPageUrl['Params']): Promise<string> {
  return axios.get('/couponPack/partnerUrl', { params });
}

/**
 * 获取客服跳转链接
 */
export type QueryCustomerServiceUrlType = DefineService<
  paths,
  '/loan/getCustomerServiceInfo',
  'get'
>;
export function queryCustomerServiceUrl(
  channelid: string,
): Promise<QueryCustomerServiceUrlType['Result']> {
  return axios.get('/loan/getCustomerServiceInfo', {
    params: {
      channelid,
    },
  });
}

/**
 * 获取注销权益列表
 */
export type QueryDetainmentRightsListType = DefineService<paths, '/user/cancel/new/strategies'>;
export function queryDetainmentRightsList(): Promise<QueryDetainmentRightsListType['Result']> {
  return axios.post('/user/cancel/new/strategies');
}

/**
 * 查询联系客服弹窗  坚持注销入口是否展示
 */
export type QueryCancelEntryVisibilityType = DefineService<
  paths,
  '/user/cancel/new/entry/visibility'
>;
export function queryCancelEntryVisibility(): Promise<QueryCancelEntryVisibilityType['Result']> {
  return axios.post('/user/cancel/new/entry/visibility');
}

/**
 * 执行用户所选挽留权益
 */
export type ExecuteDetainmentRightsType = DefineService<paths, '/user/cancel/new/strategy/execute'>;
export function executeDetainmentRights(
  params: ExecuteDetainmentRightsType['Params'],
): Promise<ExecuteDetainmentRightsType['Result']> {
  return axios.post('/user/cancel/new/strategy/execute', params);
}

/**
 * 轮询查询挽留策略结果
 */
export type QueryDetainmentStrategyResultType = DefineService<
  paths,
  '/user/cancel/new/strategy/result'
>;
export function queryDetainmentStrategyResult(options?: {
  loading?: boolean;
}): Promise<QueryDetainmentStrategyResultType['Result']> {
  return axios.post('/user/cancel/new/strategy/result', null, {
    __interceptorOptions__: {
      loading: options?.loading ?? true,
    },
  });
}

/**
 * 导流权益请求
 */
export type ExecuteDetainmentDiversionFlowType = DefineService<paths, '/user/cancel/new/diversion'>;
export function executeDetainmentDiversionFlow(): Promise<
  ExecuteDetainmentDiversionFlowType['Result']
> {
  return axios.post('/user/cancel/new/diversion');
}

/**
 * 领取注销挽留降息券
 */
export type ExecuteDetainmentCouponReduceInterestType = DefineService<
  paths,
  '/user/cancel/new/discount/coupon/receive'
>;
export function executeDetainmentCouponReduceInterest(
  params: ExecuteDetainmentCouponReduceInterestType['Params'],
): Promise<ExecuteDetainmentCouponReduceInterestType['Result']> {
  return axios.post('/user/cancel/new/discount/coupon/receive', params);
}
